import { apiUrl, deleteJson, getJson, patchJson, postJson, uploadForm } from './client'
import type { BatchView, CommitResult, OnboardingHome, Reconciliation, StagedContact, UploadAccepted } from '../types/onboarding'

// Contact onboarding: an upload is a PROPOSAL a person confirms, never an assertion. Nothing reaches
// the contacts until commit. The only place fetch lives is client.ts; this is the typed surface.
const base = (spoke: string) => `/api/spokes/${encodeURIComponent(spoke)}/scout/onboarding`
const row = (spoke: string, batchId: string, stagedId: string) =>
  `${base(spoke)}/${encodeURIComponent(batchId)}/contacts/${encodeURIComponent(stagedId)}`

export interface EditBody {
  name?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  companyName?: string | null
  title?: string | null
  phone?: string | null
  notes?: string | null
}

export const onboardingApi = {
  home: (spoke: string) => getJson<OnboardingHome>(base(spoke)),
  batch: (spoke: string, batchId: string) => getJson<BatchView>(`${base(spoke)}/${encodeURIComponent(batchId)}`),
  discard: (spoke: string, batchId: string) => deleteJson<void>(`${base(spoke)}/${encodeURIComponent(batchId)}`),

  upload: (spoke: string, file: File, onProgress?: (fraction: number) => void) => {
    const form = new FormData()
    form.append('file', file, file.name)
    return uploadForm<UploadAccepted>(`${base(spoke)}/upload`, form, onProgress)
  },

  accept: (spoke: string, batchId: string, stagedId: string) => postJson<StagedContact>(`${row(spoke, batchId, stagedId)}/accept`),
  /** The reason is required: the API refuses the call without one, so the control never sends an empty one. */
  reject: (spoke: string, batchId: string, stagedId: string, reason: string) =>
    postJson<StagedContact>(`${row(spoke, batchId, stagedId)}/reject`, { reason }),
  edit: (spoke: string, batchId: string, stagedId: string, body: EditBody) => patchJson<StagedContact>(row(spoke, batchId, stagedId), body),

  acceptUnambiguous: (spoke: string, batchId: string) => postJson<BatchView>(`${base(spoke)}/${encodeURIComponent(batchId)}/accept-unambiguous`),
  commit: (spoke: string, batchId: string) => postJson<CommitResult>(`${base(spoke)}/${encodeURIComponent(batchId)}/commit`),
  /** His priority-contacts workbook against what is on file: matched, new, targets, conflicts, set aside. */
  reconciliation: (spoke: string, batchId: string) => getJson<Reconciliation>(`${base(spoke)}/${encodeURIComponent(batchId)}/reconciliation`),

  /** The Intapp-shaped workbook, as the browser should load it (a download, not a fetch). */
  exportUrl: (spoke: string, batchId: string) => apiUrl(`${base(spoke)}/${encodeURIComponent(batchId)}/export`),
}
